import { Store } from "@ngrx/store";
import * as Sentry from "@sentry/capacitor";
import * as AppActions from "src/app/store/app/actions";
import { combineLatest, filter, firstValueFrom, Observable } from "rxjs";
import * as FromApp from "src/app/store/app/selectors";

import { RootState } from "./store";
import { AppRoutes } from "./enums";
import { Feature, Program, ProgramIntegration } from "./services/api";
import { resolveLanguage } from "./utils/resolve-language";

export const initializeApp =
  (store: Store<RootState>): Function =>
    async () => {
      try {
        const slug = window.location.pathname.split("/").filter(Boolean).shift();
        if (slug === AppRoutes.notFound) return;
        if (!slug) {
          document.querySelector(".--root-loading")?.classList.remove("--root-loading");
          window.location.href = AppRoutes.notFound;
          return;
        }

        // Boot language candidate: ?lang= wins (embeds force it), else navigator.
        const candidate = new URLSearchParams(window.location.search).get("lang") || navigator.language;
        const serverProgram = await firstValueFrom(store.select(FromApp.selectProgram));

        if (serverProgram) {
          // Hydrated path: program available immediately, resolve language before any request fires.
          store.dispatch(AppActions.changeSessionLanguage({
            language: resolveLanguage(candidate, serverProgram),
            silent: true,
          }));
          // Trigger side effects (CSS vars, favicon, title) for hydrated program
          store.dispatch(AppActions.getProgramSuccess({ program: serverProgram }));
        } else {
          // No server state (local dev, Cloudflare unavailable). Resolve language from the
          // candidate first (clamped to supported languages) so every request — including
          // the initial program/integrations/features/tiers fetches — carries the right
          // X-APP-LANGUAGE. Re-resolve once the program lands in case program.enabledLanguages
          // narrows the choice.
          store.dispatch(AppActions.changeSessionLanguage({
            language: resolveLanguage(candidate),
            silent: true,
          }));

          const program$: Observable<Program> = store.select(FromApp.selectProgram);
          const programIntegrations$: Observable<ProgramIntegration[]> = store.select(FromApp.selectProgramIntegrations);
          const features$: Observable<Feature[]> = store.select(FromApp.selectFeatures);

          store.dispatch(AppActions.getProgram({ slug }));
          store.dispatch(AppActions.getProgramIntegrations({ programId: slug }));
          store.dispatch(AppActions.getFeatures());
          store.dispatch(AppActions.getTiers({ programId: slug }));
          store.dispatch(AppActions.getEngagementFlowsWithProgramEngagementFlow({ programId: slug }));

          const [program] = await firstValueFrom(
            combineLatest([program$, programIntegrations$, features$]).pipe(
              filter(([program, programIntegrations, features]) =>
                !!program && !!programIntegrations?.length && !!features?.length,
              ),
            ),
          );

          const refined = resolveLanguage(candidate, program);
          store.dispatch(AppActions.changeSessionLanguage({ language: refined, silent: true }));
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        document.querySelector(".--root-loading")?.classList.remove("--root-loading");
      }
    };
